import React, { useState } from "react";
import { Providers } from "@tripian/model";
import moment from "moment";
import { displayableLivnInvoice } from "../../../utils/livnPricing";
import { livnBookingCancellationPolicy, livnCancellationPolicyLines } from "../../../utils/livnCancellation";
import { downloadLivnTicketsPdf } from "../../../utils/livnTicketPdf";

interface ILivnBookingTickets {
  bookings: Providers.Livn.Booking[];
  markupPerc?: number;
  tourName?: string;
  fallbackTravelDate?: string;
  fallbackTimeLabel?: string;
}

/**
 * Livn sends the cancellation terms either as free text or as a rule ladder, where each rule
 * states the fee that applies from `after` onwards. The ladder is turned into plain intervals
 * so a customer can read what cancelling now would cost.
 */
const CancellationPolicyView: React.FC<{ policy?: Providers.Livn.CancellationPolicy }> = ({ policy }) => {
  const lines = livnCancellationPolicyLines(policy);
  if (lines.length === 0) return null;
  return (
    <div className="text-xs text-gray-600 space-y-0.5">
      <p className="font-medium text-gray-700">Cancellation Policy</p>
      {lines.map((line, i) => (
        <p key={i}>{line}</p>
      ))}
    </div>
  );
};

const ResourceLinks: React.FC<{ resources: Providers.Livn.ExternalResource[] }> = ({ resources }) =>
  resources.length === 0 ? null : (
    <div className="flex flex-wrap gap-2 mt-2">
      {resources.map((r, i) => (
        <a
          key={i}
          href={r.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs px-3 py-1.5 rounded-full border border-primary-color text-primary-color hover:bg-primary-color/5"
        >
          {r.caption || "View ticket"}
          {r.printRequired ? " (print required)" : ""}
        </a>
      ))}
    </div>
  );

const LivnBookingTickets: React.FC<ILivnBookingTickets> = ({
  bookings,
  markupPerc,
  tourName,
  fallbackTravelDate,
  fallbackTimeLabel,
}) => {
  const [downloadState, setDownloadState] = useState<"idle" | "busy" | "failed">("idle");

  // Offered only once the supplier has issued at least one ticket — a "tickets" PDF with
  // no tickets in it would be worse than the on-screen "being issued" message.
  const hasIssuedTickets = bookings.some(b => (b.tickets ?? []).length > 0);

  const handleDownload = async () => {
    setDownloadState("busy");
    try {
      await downloadLivnTicketsPdf({ bookings, tourName, markupPerc, fallbackTravelDate, fallbackTimeLabel });
      setDownloadState("idle");
    } catch {
      setDownloadState("failed");
    }
  };

  // Tickets are numbered across the whole reservation: a product with several fare types comes
  // back as one booking per fare, so a per-booking index would label traveller 1 of every
  // booking identically.
  let ticketCounter = 0;

  return (
    <>
      {hasIssuedTickets && (
        <div className="flex items-center justify-end gap-2">
          {downloadState === "failed" && (
            <span className="text-xs text-red-500">The ticket could not be generated. Please try again.</span>
          )}
          <button
            className="px-4 py-2 rounded-lg border border-primary-color text-primary-color text-sm font-medium hover:bg-primary-color/5 disabled:opacity-50"
            onClick={handleDownload}
            disabled={downloadState === "busy"}
          >
            {downloadState === "busy" ? "Preparing ticket…" : "⬇ Download tickets (PDF)"}
          </button>
        </div>
      )}
      {bookings.map(booking => {
        const invoiceView = displayableLivnInvoice(booking.invoice, markupPerc);

        const allResources = booking.externalResources?.externalResources ?? [];
        // Resources without ticketUuids cover the whole booking; the rest belong to one ticket.
        const bookingResources = allResources.filter(r => !r.ticketUuids?.length);
        const tickets = booking.tickets ?? [];

        return (
          <div key={booking.id} className="border border-gray-200 rounded-xl p-4">
            <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs text-gray-500">
              {booking.livnReference && (
                <span>
                  Booking ref: <strong className="text-gray-700">{booking.livnReference}</strong>
                </span>
              )}
              {booking.supplierReference && <span>Supplier ref: {booking.supplierReference}</span>}
              {booking.partyName && <span>Lead traveller: {booking.partyName}</span>}
              {booking.partyEmailAddress && <span>{booking.partyEmailAddress}</span>}
              {booking.confirmed && <span>Confirmed {moment(booking.confirmed).format("MMM D, YYYY HH:mm")}</span>}
            </div>

            {booking.productName && <p className="text-sm font-semibold text-gray-800 mb-2">{booking.productName}</p>}

            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              {tickets.length > 0 ? `Ticket Details (${tickets.length})` : "Ticket Details"}
            </h3>

            {tickets.length === 0 ? (
              <p className="text-sm text-gray-500 mb-2">
                Your tickets are being issued by the supplier and will appear here — and in your reservations — as soon
                as they are available.
              </p>
            ) : (
              tickets.map(ticket => {
                ticketCounter += 1;
                const passengers = ticket.passengerDetails ?? [];
                const isGroupTicket = passengers.length > 1;
                const ticketResources = allResources.filter(r =>
                  ticket.uuid ? r.ticketUuids?.includes(ticket.uuid) : false
                );
                const pickup = ticket.pickupDetails;

                return (
                  <div key={ticket.id} className="flex items-start gap-3 p-3 mb-2 border border-gray-100 rounded-lg bg-gray-50">
                    {ticket.barcodeDetails?.barcodeData && (
                      <div className="flex-shrink-0 text-center">
                        <img
                          src={ticket.barcodeDetails.barcodeData}
                          alt={`Ticket ${ticketCounter} barcode`}
                          className="w-24 h-24 bg-white p-1 rounded border border-gray-200"
                        />
                        {ticket.barcodeDetails.barcodeRaw && (
                          <p className="text-[10px] text-gray-400 mt-1 break-all max-w-[96px]">
                            {ticket.barcodeDetails.barcodeRaw}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-800">
                        {isGroupTicket ? `Group Ticket (${passengers.length} travellers)` : `Ticket ${ticketCounter}`}
                      </p>

                      {passengers.length > 0 && (
                        <ul className="mt-1">
                          {passengers.map((p, i) => (
                            <li key={i} className="text-sm text-gray-700">
                              {p.name}
                            </li>
                          ))}
                        </ul>
                      )}

                      {(ticket.productDetails ?? []).map((p, i) => (
                        <p key={i} className="text-xs text-gray-500 mt-0.5">
                          {p.name}
                          {p.startTime ? ` · ${p.startTime.slice(0, 5)}` : ""}
                        </p>
                      ))}

                      <div className="flex flex-wrap gap-x-3 text-xs text-gray-400 mt-0.5">
                        <span>
                          {ticket.travelDate
                            ? moment(ticket.travelDate).format("MMM D, YYYY")
                            : fallbackTravelDate
                            ? moment(fallbackTravelDate).format("MMM D, YYYY")
                            : null}
                        </span>
                        {!ticket.productDetails?.[0]?.startTime && fallbackTimeLabel && <span>{fallbackTimeLabel}</span>}
                        {ticket.supplierName && <span>{ticket.supplierName}</span>}
                      </div>

                      {pickup && (pickup.pickupLocation || pickup.notes || pickup.dropOffLocation) && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-800 space-y-0.5">
                          {pickup.pickupLocation && (
                            <p>
                              <span className="font-semibold">Pickup: </span>
                              {pickup.pickupLocation}
                              {pickup.pickupTime ? ` at ${pickup.pickupTime.slice(0, 5)}` : ""}
                            </p>
                          )}
                          {pickup.notes && <p>{pickup.notes}</p>}
                          {pickup.dropOffLocation && (
                            <p>
                              <span className="font-semibold">Drop-off: </span>
                              {pickup.dropOffLocation}
                            </p>
                          )}
                          {pickup.dropoffNotes && <p>{pickup.dropoffNotes}</p>}
                        </div>
                      )}

                      {(ticket.supplierEmailRes || ticket.supplierPhoneRes) && (
                        <p className="text-xs text-gray-500 mt-1">
                          Supplier contact: {[ticket.supplierEmailRes, ticket.supplierPhoneRes].filter(Boolean).join(" · ")}
                        </p>
                      )}

                      {/* Sahada ödenecek ücretler (ör. "20.0 AUD National park levy…").
                          Bilet fiyatına dahil değil, o yüzden ayrıca ve vurgulu gösteriliyor. */}
                      {ticket.localFees && (
                        <p className="text-xs font-medium text-amber-700 mt-1">
                          Payable locally: {ticket.localFees}
                        </p>
                      )}

                      {ticket.printRequired && (
                        <p className="text-xs font-medium text-amber-700 mt-1">
                          This ticket must be printed and presented on the day.
                        </p>
                      )}

                      {ticket.specialNotes && (
                        <p className="text-xs text-gray-500 mt-1 whitespace-pre-line">{ticket.specialNotes}</p>
                      )}

                      <ResourceLinks resources={ticketResources} />
                    </div>
                  </div>
                );
              })
            )}

            {invoiceView && (
              <div className="border-t border-gray-100 mt-2 pt-2">
                {invoiceView.lines.map((line, i) => (
                  <div key={i} className="flex justify-between text-xs text-gray-500">
                    <span>
                      {line.title} × {line.quantity}
                    </span>
                    <span>
                      {line.displayed.amount} {line.displayed.currency}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-semibold text-gray-700 pt-1">
                  <span>Total paid</span>
                  <span>
                    {invoiceView.total} {invoiceView.currency}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-2">
              <CancellationPolicyView policy={livnBookingCancellationPolicy(booking)} />
            </div>

            <ResourceLinks resources={bookingResources} />
          </div>
        );
      })}
    </>
  );
};

export default LivnBookingTickets;
